/**
 * AgeGateScreen — First-impression COPPA-style age confirmation.
 *
 * Asks "Are you 13 or older?" with a single yes/no answer. Persists the
 * answer to localStorage so returning users skip it. The result drives
 * whether SignInScreen renders in "self" mode (13+) or "parent" mode (under 13).
 *
 * NOTE: A binary yes/no is the minimum bar for COPPA's age gating. A
 * neutral year-of-birth picker is stronger (FTC-recommended pattern).
 * Worth revisiting before public launch — see CLAUDE.md for the upgrade
 * path.
 */

import { useState } from "react";

const AGE_GATE_KEY  = "moneyschool_age_gate_v1";
const IS_ADULT_KEY  = "moneyschool_is_adult_v1";

export function loadAgeGate(): { answered: boolean; isAdult: boolean } {
  try {
    const answered = localStorage.getItem(AGE_GATE_KEY) === "1";
    const isAdult  = localStorage.getItem(IS_ADULT_KEY) === "1";
    return { answered, isAdult };
  } catch {
    return { answered: false, isAdult: false };
  }
}

export function saveAgeGate(isAdult: boolean) {
  try {
    localStorage.setItem(AGE_GATE_KEY, "1");
    localStorage.setItem(IS_ADULT_KEY, isAdult ? "1" : "0");
  } catch { /* quota / disabled */ }
}

export function clearAgeGate() {
  try {
    localStorage.removeItem(AGE_GATE_KEY);
    localStorage.removeItem(IS_ADULT_KEY);
  } catch { /* */ }
}

interface Props {
  onAnswer: (isAdult: boolean) => void;
}

export default function AgeGateScreen({ onAnswer }: Props) {
  const [busy, setBusy] = useState(false);

  function pick(isAdult: boolean) {
    if (busy) return;
    setBusy(true);
    saveAgeGate(isAdult);
    onAnswer(isAdult);
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={brandStyle}>MoneyMission</div>

        <h1 style={titleStyle}>Before we start…</h1>

        <p style={subtitleStyle}>
          Quick question — this helps us show you the right experience and keep things safe.
        </p>

        <div style={questionBoxStyle}>
          <div style={questionStyle}>Are you 13 or older?</div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 18 }}>
            <button
              onClick={() => pick(true)}
              disabled={busy}
              style={{ ...primaryButtonStyle, background: "linear-gradient(135deg,#0d9488,#0891b2)" }}
            >
              Yes, I'm 13 or older
            </button>

            <button
              onClick={() => pick(false)}
              disabled={busy}
              style={{ ...primaryButtonStyle, background: "#fff", color: "#0d9488", border: "2px solid #0d9488" }}
            >
              No, I'm under 13
            </button>
          </div>
        </div>

        <p style={fineprintStyle}>
          If you're under 13, a parent or guardian needs to set up your account before you can play.
          We never collect names, addresses, or school info from kids — see our privacy policy.
        </p>
      </div>
    </div>
  );
}

/* ─ Styles ───────────────────────────────────────────────────────────────── */
const containerStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "linear-gradient(135deg, #042f2e 0%, #134e4a 60%, #0d9488 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
  fontFamily: "Nunito, system-ui, sans-serif",
};

const cardStyle: React.CSSProperties = {
  background: "#fff",
  borderRadius: 28,
  padding: "28px 24px",
  maxWidth: 460,
  width: "100%",
  boxShadow: "0 30px 80px rgba(0, 0, 0, 0.30)",
};

const brandStyle: React.CSSProperties = {
  fontSize: 22,
  fontWeight: 900,
  background: "linear-gradient(135deg, #0d9488, #f59e0b)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
  fontFamily: "Poppins, system-ui, sans-serif",
  textAlign: "center",
  marginBottom: 8,
};

const titleStyle: React.CSSProperties = {
  fontSize: 26,
  fontWeight: 900,
  color: "#0f172a",
  margin: "4px 0 8px",
  textAlign: "center",
  fontFamily: "Fredoka One, system-ui, sans-serif",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 14,
  color: "#475569",
  margin: "0 0 22px",
  lineHeight: 1.5,
  textAlign: "center",
};

const questionBoxStyle: React.CSSProperties = {
  background: "#f0fdfa",
  border: "2px solid #99f6e4",
  borderRadius: 18,
  padding: "20px 18px",
};

const questionStyle: React.CSSProperties = {
  fontSize: 17,
  fontWeight: 800,
  color: "#134e4a",
  textAlign: "center",
};

const primaryButtonStyle: React.CSSProperties = {
  width: "100%",
  padding: 16,
  fontSize: 16,
  fontWeight: 800,
  color: "#fff",
  border: "none",
  borderRadius: 14,
  cursor: "pointer",
  minHeight: 50,
  fontFamily: "Nunito, system-ui, sans-serif",
};

const fineprintStyle: React.CSSProperties = {
  fontSize: 12,
  color: "#94a3b8",
  marginTop: 20,
  lineHeight: 1.6,
  textAlign: "center",
};
